import { useDispatch, useSelector } from 'react-redux';
import { readinessActions as A } from '../../store/readinessSlice';
import ScoreRing from './ScoreRing';
import {
  matchPrograms,
  bestFitRecommendation,
  resultStats,
  improvementList,
  scoreHeadlineFor,
  scoreBlurbFor,
} from '../../utils/readinessAssessment';

/** Compliance-safe headline per recommendation state — never "approved". */
const BESTFIT_COPY = {
  single_best_fit: 'Based on everything you shared, this appears to be the strongest fit:',
  pricing_required: 'Based on your answers, this looks like the strongest fit today — with one live-pricing comparison worth making:',
  multiple_candidates: 'A few strong candidates remain — this one currently leads:',
  collecting_data: 'Here\u2019s the current front-runner based on what you\u2019ve shared so far:',
};

/**
 * Results screen: animated score reveal, Provide/Prove/Verify stage cards,
 * segmented category breakdown, selectable program-match cards,
 * improvement list, and CTAs.
 */
export default function ResultsScreen({ breakdown, controller }) {
  const dispatch = useDispatch();
  const { answers, selectedProgram, appDone } = useSelector((s) => s.readiness);
  const { score, verifiedScore, scoreColor, cats } = breakdown;

  const { stats, dti } = resultStats(answers, { done: true, verifiedScore, score });
  const improvements = improvementList(answers, { score, verifiedScore, dti });
  const programs = matchPrograms(answers);
  const rec = bestFitRecommendation(answers);

  return (
    <main data-screen-label="Readiness results" className="rdz-results-main">
      <div className="rdz-results">
        {/* Score hero */}
        <div className="rdz-hero">
          <ScoreRing score={score} color={scoreColor} size="big">
            <div className="rdz-hero__ring-num">
              <span className="rdz-hero__score">{score}</span>
              <span className="rdz-hero__of">of 100</span>
            </div>
          </ScoreRing>
          <div className="rdz-hero__right">
            <div className="rdz-hero__overline">Your Readiness Score</div>
            <h1 className="rdz-hero__headline">{scoreHeadlineFor(score)}</h1>
            <p className="rdz-hero__blurb">{scoreBlurbFor(score)}</p>
            <div className="rdz-hero__stats">
              {stats.map((s) => (
                <div className="rdz-stat-chip" key={s.label}>
                  <div className="rdz-stat-chip__label">{s.label}</div>
                  <div className="rdz-stat-chip__value">{s.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Category breakdown */}
        <div className="rdz-card">
          <h2 className="rdz-card__title">Where your score comes from</h2>
          <p className="rdz-card__sub">
            Information is a great start &mdash; but its full value needs proof. Each category pays
            out in three stages.
          </p>
          <div className="rdz-stages">
            <div className="rdz-stage">
              <div className="rdz-stage__title">1 &middot; Provide &mdash; 60%</div>
              <div className="rdz-stage__body">Share your information — that&apos;s what you&apos;ve been doing here.</div>
            </div>
            <div className="rdz-stage">
              <div className="rdz-stage__title">2 &middot; Prove &mdash; 25%</div>
              <div className="rdz-stage__body">Upload the documents that back it up — pay stubs, statements, returns.</div>
            </div>
            <div className="rdz-stage">
              <div className="rdz-stage__title">3 &middot; Verify &mdash; 15%</div>
              <div className="rdz-stage__body">We confirm the proof matches what you entered — lenders trust a verified file.</div>
            </div>
          </div>
          <div className="rdz-results__cats">
            {cats.map((c) => (
              <div key={c.id}>
                <div className="rdz-rcat__head">
                  <span className="rdz-rcat__label">{c.label}</span>
                  <span className="rdz-rcat__pts">{c.ptsLabel}</span>
                </div>
                <div className="rdz-rcat__track">
                  <div style={{ height: '100%', background: c.color, width: c.pct }} />
                  <div style={{ height: '100%', background: c.colorProof, width: c.pctProof }} />
                  <div style={{ height: '100%', background: c.colorVerif, width: c.pctVerif }} />
                </div>
                {c.gainLabel && <div className="rdz-rcat__gain">{c.gainLabel}</div>}
              </div>
            ))}
          </div>
        </div>

        {/* Best-fit product (decision engine) */}
        {rec && rec.best && (
          <div className="rdz-card rdz-bestfit">
            <div className="rdz-bestfit__overline">
              Best-fit loan product
              <span className="rdz-bestfit__pool">
                {rec.progress.total}+ products analyzed &middot; {rec.progress.remaining} remained compatible
              </span>
            </div>
            <p className="rdz-bestfit__lede">{BESTFIT_COPY[rec.state] || BESTFIT_COPY.multiple_candidates}</p>
            <h2 className="rdz-bestfit__name">{rec.best.displayName}</h2>
            {rec.equivalents.length > 0 && (
              <p className="rdz-bestfit__equivalents">
                Equally strong: {rec.equivalents.map((e) => e.displayName).join(' · ')} — functionally
                the same for you; lenders may price each a little differently, and that&apos;s where
                competition works in your favor.
              </p>
            )}
            {rec.best.reasons.length > 0 && (
              <ul className="rdz-bestfit__reasons">
                {rec.best.reasons.map((r) => (
                  <li key={r} className="rdz-bestfit__reason">{r}</li>
                ))}
              </ul>
            )}
            {rec.best.cautions.length > 0 && (
              <ul className="rdz-bestfit__cautions">
                {rec.best.cautions.map((c) => (
                  <li key={c} className="rdz-bestfit__caution">{c}</li>
                ))}
              </ul>
            )}
            {rec.state === 'pricing_required' && rec.compareWithPricing.length > 0 && (
              <div className="rdz-bestfit__compare">
                Worth pricing side by side: <strong>{rec.compareWithPricing[0].displayName}</strong>.
                Given your time horizon, it could win — but only live rates can prove it, and
                we never assume an adjustable rate is lower.
              </div>
            )}
            <div className="rdz-bestfit__disclaimer">
              Based on the information provided. This is a product match, not a credit decision —
              lender underwriting may differ.
            </div>
          </div>
        )}

        {/* Program matches */}
        <div>
          <h2 className="rdz-programs__title">Programs that fit your profile</h2>
          <p className="rdz-programs__sub">
            Matched from Owen&apos;s analysis of your answers. Sample rates for illustration &mdash;
            live pricing comes from competing lenders.
          </p>
          <div className="rdz-programs__grid">
            {programs.map((p) => {
              const selected = p.name === selectedProgram;
              return (
                <div
                  key={p.name}
                  role="button"
                  tabIndex={0}
                  className={
                    'rdz-program'
                    + (selected ? ' rdz-program--selected' : p.best ? ' rdz-program--best' : '')
                  }
                  onClick={() => dispatch(A.selectProgram(p.name))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') dispatch(A.selectProgram(p.name));
                  }}
                >
                  {p.best && <span className="rdz-float-badge">Best match</span>}
                  <div className="rdz-program__name">{p.name}</div>
                  <div className="rdz-program__rate-row">
                    <span className="rdz-program__rate">{p.rate}</span>
                    <span className="rdz-program__rate-label">est. rate</span>
                  </div>
                  <div className="rdz-program__blurb">{p.blurb}</div>
                  <div className="rdz-program__sel" style={{ color: selected ? '#4f46e5' : '#a1a1aa' }}>
                    {selected ? '\u2713 Selected' : 'Tap to select'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Improvements */}
        <div className="rdz-card">
          <h2 className="rdz-card__title">Fastest ways to raise your score</h2>
          <p className="rdz-card__sub">
            None of these are required &mdash; but each one strengthens your position before lenders
            compete for you.
          </p>
          <div className="rdz-improvements">
            {improvements.map((im) => (
              <div className="rdz-improvement" key={im.text}>
                <span className="rdz-improvement__impact">{im.impact}</span>
                <span className="rdz-improvement__text">{im.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTAs */}
        {appDone && (
          <div className="rdz-saved-banner">
            <span className="rdz-saved-banner__check">&#10003;</span>
            <span className="rdz-saved-banner__text">
              Application saved to your account &mdash; sign back in anytime with a secure code we
              email you. No password needed.
            </span>
          </div>
        )}
        <div className="rdz-ctas">
          {!appDone && (
            <button type="button" className="rdz-btn-cta" onClick={() => controller.continueApp()}>
              Continue my application &mdash; {selectedProgram || ''}
            </button>
          )}
          <button type="button" className="rdz-btn-secondary" onClick={() => dispatch(A.gotoMatches())}>
            See my lender matches
          </button>
          <button type="button" className="rdz-btn-secondary" onClick={() => dispatch(A.openReview())}>
            Review my answers
          </button>
          <button type="button" className="rdz-btn-text" onClick={() => dispatch(A.openReport())}>
            Email my full report
          </button>
          <button type="button" className="rdz-btn-text" onClick={() => controller.restart()}>
            Start over
          </button>
        </div>
      </div>
    </main>
  );
}
